import React from 'react';
import { Container, Typography, Paper, Box, Grid, Button, ButtonGroup } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Assessment as AssessmentIcon,
  RuleFolder as RuleFolderIcon,
  Timeline as TimelineIcon,
  Explore as ExploreIcon,
  Assignment as AssignmentIcon,
  Chat as ChatIcon,
  CompareArrows as CompareArrowsIcon,
  Policy as PolicyIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

const MainFeatureCard = ({ icon: Icon, title, description, onClick }) => (
  <Paper 
    elevation={3}
    sx={{ 
      p: 4,
      height: '100%',
      transition: 'transform 0.2s, box-shadow 0.2s',
      cursor: 'pointer',
      backgroundColor: '#f8f9fa',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 4,
      },
    }}
    onClick={onClick}
  >
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      textAlign: 'center',
      height: '100%'
    }}>
      <Icon sx={{ fontSize: 56, color: '#2e7d32', mb: 3 }} />
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#2e7d32' }}>
        {title}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: '80%' }}>
        {description}
      </Typography>
    </Box>
  </Paper>
);

const SecondaryFeatureCard = ({ icon: Icon, title, description, onClick }) => (
  <Paper 
    elevation={2}
    sx={{ 
      p: 3,
      height: '100%',
      transition: 'transform 0.2s, box-shadow 0.2s',
      cursor: 'pointer',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 3,
      },
    }}
    onClick={onClick}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
      <Icon sx={{ fontSize: 40, color: '#2e7d32', mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
  </Paper>
);

const UnderwriterHomePage = () => {
  const navigate = useNavigate();

  const mainFeatures = [
    {
      icon: AssessmentIcon,
      title: "Statistics Dashboard",
      description: "Comprehensive statistics and analytics for policy submissions and approvals",
      onClick: () => navigate('/underwriter/statistics')
    },
    {
      icon: RuleFolderIcon,
      title: "Policy Review",
      description: "Streamlined policy review process with integrated compliance checks and decision support",
      onClick: () => navigate('/underwriter/policy-review')
    }
  ];

  const secondaryFeatures = [
    {
      icon: TimelineIcon,
      title: "Broker Quality Metrics",
      description: "Analyze broker performance and policy quality",
      onClick: () => navigate('/underwriter/quality-metrics')
    },
    {
      icon: AssignmentIcon,
      title: "Task Management",
      description: "Organize and track underwriting tasks and assignments",
      onClick: () => navigate('/underwriter/tasks')
    },
    {
      icon: ChatIcon,
      title: "AI Assistant",
      description: "Get instant answers to underwriting questions",
      onClick: () => navigate('/ai-chat')
    }
  ];

  return (
    <Container maxWidth="xl">
      <Box 
        sx={{ 
          mt: 8,
          mb: 6,
          textAlign: 'center',
          position: 'relative',
        }}
      >
        <Box 
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            mb: 2,
          }}
        >
          <ExploreIcon sx={{ fontSize: 48, color: '#2e7d32' }} />
          <Typography 
            variant="h2" 
            component="h1"
            sx={{ 
              fontWeight: 600,
              color: '#2e7d32',
            }}
          >
            UW Workspace
          </Typography>
        </Box>
        
        <Typography 
          variant="h5" 
          component="h2" 
          sx={{ 
            color: '#43a047',
            mb: 3,
            fontWeight: 400,
          }}
        >
          Streamline Your Underwriting Process
        </Typography>

        <Box sx={{ 
          display: 'flex',
          justifyContent: 'center',
          mb: 6,
        }}>
          <ButtonGroup variant="outlined">
            <Button
              startIcon={<CompareArrowsIcon />}
              onClick={() => navigate('/')}
              sx={{
                color: '#2e7d32',
                borderColor: '#2e7d32',
                px: 4,
                py: 1,
                '&:hover': {
                  borderColor: '#1b5e20',
                  bgcolor: 'rgba(46, 125, 50, 0.04)'
                }
              }}
            >
              Switch to Broker
            </Button>
          </ButtonGroup>
        </Box>

        {/* Main Features */}
        <Box sx={{ mb: 10 }}>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 4, 
              color: '#2e7d32',
              fontWeight: 500,
              textAlign: 'left'
            }}
          >
            Core Functions
          </Typography>
          <Grid container spacing={4}>
            {mainFeatures.map((feature, index) => (
              <Grid item xs={12} md={6} key={index}>
                <MainFeatureCard {...feature} />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Secondary Features */}
        <Box>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 4, 
              color: '#2e7d32',
              fontWeight: 500,
              textAlign: 'left'
            }}
          >
            Additional Tools
          </Typography>
          <Grid container spacing={3}>
            {secondaryFeatures.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <SecondaryFeatureCard {...feature} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </Container>
  );
};

export default UnderwriterHomePage; 